import React from "react";
import axios from "axios";
import { Gift, Copy, Check, Share2, Mail, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReferralCard — shows the customer's permanent referral code, share link, and stats.
 * Used on PaymentSuccessPage + TrackPage. Requires customer email to fetch/issue the code.
 */
export default function ReferralCard({ customerEmail }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!customerEmail) return;
    let cancel = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/referral/me`, { params: { email: customerEmail } });
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [customerEmail]);

  const copyLink = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* ignore */
    }
  };

  const shareNative = async () => {
    if (!data?.share_url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "EMA Kurer · 50 kr rabat",
          text: "Brug min anbefalingskode og få 50 kr rabat på din første kurer-booking hos EMA Kurer:",
          url: data.share_url,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  const shareEmail = () => {
    if (!data?.share_url) return;
    const subject = encodeURIComponent("Jeg anbefaler EMA Kurer — få 50 kr rabat");
    const body = encodeURIComponent(
      `Hej!\n\nJeg har brugt EMA Kurer til min kurer-levering og var meget tilfreds. Hvis du booker via mit link, får du 50 kr rabat:\n\n${data.share_url}\n\n(Jeg får også 50 kr som tak når du har booket — vi vinder begge to!)`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareSms = () => {
    if (!data?.share_url) return;
    const text = encodeURIComponent(
      `Du kan få 50 kr rabat på en kurer hos EMA Kurer via mit link: ${data.share_url}`
    );
    window.location.href = `sms:?body=${text}`;
  };

  if (!customerEmail) return null;
  if (loading && !data) return null;
  if (error || !data) return null;

  const stats = data.stats || {};

  return (
    <div className="rounded-2xl border-2 border-brand-red bg-gradient-to-br from-white to-red-50 p-5 sm:p-6" data-testid="referral-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-brand-red mb-1">
            <Gift size={14} /> Inviter & spar
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-bold text-brand-dark leading-tight">
            Inviter en ven, få 50 kr i rabat
          </h3>
        </div>
        {stats.completed_count > 0 && (
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold text-brand-red">{stats.total_earned_dkk?.toFixed(0)} kr</div>
            <div className="text-[10px] uppercase tracking-wider text-brand-text2">tjent</div>
          </div>
        )}
      </div>

      <p className="text-sm text-brand-text2 mb-4">
        Når en ven booker via din kode, får I <strong>begge 50 kr</strong> i rabat. Din rabat kommer som en kupon
        til næste booking, automatisk pr. email.
      </p>

      {/* Code box */}
      <div className="bg-brand-dark text-white rounded-xl p-4 mb-3" data-testid="referral-code-box">
        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">DIN KODE</div>
        <div className="flex items-center justify-between gap-3">
          <div className="font-mono text-2xl sm:text-3xl font-bold tracking-widest">{data.code}</div>
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-red px-3 py-2 text-xs font-bold hover:bg-brand-red/90 transition-colors"
            data-testid="referral-copy-link"
          >
            {copied ? <><Check size={14} /> Kopieret!</> : <><Copy size={14} /> Kopiér link</>}
          </button>
        </div>
      </div>

      {/* Share buttons */}
      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={shareNative}
          className="flex flex-col items-center gap-1 p-3 rounded-lg border border-slate-200 hover:bg-white transition-colors"
          data-testid="referral-share-native"
        >
          <Share2 size={18} className="text-brand-dark" />
          <span className="text-xs font-semibold">Del</span>
        </button>
        <button
          onClick={shareEmail}
          className="flex flex-col items-center gap-1 p-3 rounded-lg border border-slate-200 hover:bg-white transition-colors"
          data-testid="referral-share-email"
        >
          <Mail size={18} className="text-brand-dark" />
          <span className="text-xs font-semibold">Email</span>
        </button>
        <button
          onClick={shareSms}
          className="flex flex-col items-center gap-1 p-3 rounded-lg border border-slate-200 hover:bg-white transition-colors"
          data-testid="referral-share-sms"
        >
          <MessageCircle size={18} className="text-brand-dark" />
          <span className="text-xs font-semibold">SMS</span>
        </button>
      </div>

      {/* Active rewards */}
      {stats.active_reward_codes && stats.active_reward_codes.length > 0 && (
        <div className="mt-4 rounded-xl bg-green-50 border border-green-200 p-3" data-testid="referral-active-rewards">
          <div className="text-xs font-bold text-green-800 mb-2">
            🎉 Du har {stats.active_reward_codes.length} ubrugt{stats.active_reward_codes.length === 1 ? "" : "e"} rabatkode{stats.active_reward_codes.length === 1 ? "" : "r"} klar:
          </div>
          <div className="space-y-1.5">
            {stats.active_reward_codes.map((c) => (
              <div key={c.code} className="flex items-center justify-between text-sm bg-white rounded-md px-3 py-1.5">
                <span className="font-mono font-bold text-brand-dark">{c.code}</span>
                <span className="text-green-700 font-semibold">{c.amount_dkk?.toFixed(0)} kr</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats footer */}
      {stats.invited_count > 0 && (
        <div className="mt-4 flex justify-around text-center text-xs text-brand-text2 border-t border-slate-200 pt-3">
          <div>
            <div className="font-bold text-brand-dark text-base">{stats.invited_count}</div>
            <div>inviteret</div>
          </div>
          <div>
            <div className="font-bold text-brand-dark text-base">{stats.completed_count}</div>
            <div>fuldført</div>
          </div>
          <div>
            <div className="font-bold text-brand-red text-base">{stats.total_earned_dkk?.toFixed(0)} kr</div>
            <div>i alt tjent</div>
          </div>
        </div>
      )}
    </div>
  );
}
